import React, { useState, useRef } from 'react';
import { Modal, Form, Button, Alert, Row, Col, Card, Badge, ProgressBar, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface Task {
  _id: string;
  title: string;
  description: string;
  category: string;
  rewardAmount: number;
  requirements: string[];
  instructions: string;
  estimatedTimeMinutes: number;
  priority: string;
}

interface TaskSubmissionModalProps {
  show: boolean;
  onHide: () => void;
  task: Task | null;
  onSubmissionSuccess: () => void;
}

const TaskSubmissionModal: React.FC<TaskSubmissionModalProps> = ({
  show,
  onHide,
  task,
  onSubmissionSuccess
}) => {
  // Access auth context for future use
  useAuth();
  const [submissionText, setSubmissionText] = useState('');
  const [proofFiles, setProofFiles] = useState<File[]>([]);
  const [completionTime, setCompletionTime] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // File validation
  const validateFile = (file: File): string | null => {
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Only images and documents are allowed.';
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB
      return 'File size cannot exceed 10MB.';
    }
    
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    const errors: string[] = [];

    if (selectedFiles.length > 5) {
      setError('Maximum 5 files allowed.');
      return;
    }

    selectedFiles.forEach(file => {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setProofFiles(prev => [...prev, ...validFiles].slice(0, 5));
    setError('');
  };

  const removeFile = (index: number) => {
    setProofFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!task) return;
    
    if (submissionText.trim().length < 10) {
      setError('Submission description must be at least 10 characters long.');
      return;
    }

    if (proofFiles.length === 0) {
      setError('Please upload at least one proof file.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('submissionText', submissionText.trim());
      if (completionTime) {
        formData.append('completionTime', completionTime.toString());
      }

      proofFiles.forEach(file => {
        formData.append('proofFiles', file);
      });

      await axios.post(
        `${API_BASE_URL}/task-submissions/${task._id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percentCompleted);
            }
          }
        }
      );

      setSuccess('Task submitted successfully! Your submission is now under review.');
      
      // Reset form
      setSubmissionText('');
      setProofFiles([]);
      setCompletionTime('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Call success callback after a delay
      setTimeout(() => {
        onSubmissionSuccess();
        onHide();
      }, 2000);

    } catch (error: any) {
      console.error('Task submission error:', error);
      setError(
        error.response?.data?.message || 
        'Failed to submit task. Please try again.'
      );
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const resetModal = () => {
    setSubmissionText('');
    setProofFiles([]);
    setCompletionTime('');
    setError('');
    setSuccess('');
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetModal();
      onHide();
    }
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return 'fas fa-image';
    if (file.type === 'application/pdf') return 'fas fa-file-pdf';
    return 'fas fa-file-alt';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton={!loading}>
        <Modal.Title>
          <i className="fas fa-upload me-2"></i>
          Submit Task Completion
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {task && (
          <>
            {/* Task Summary */}
            <Card className="mb-4" style={{ backgroundColor: '#f8f9fa' }}>
              <Card.Body>
                <Row>
                  <Col md={8}>
                    <h5 className="mb-2">{task.title}</h5>
                    <p className="text-muted mb-1">{task.description}</p>
                    <div className="mt-2">
                      <Badge bg="info" className="me-2">{task.category}</Badge>
                      <Badge bg="success" className="me-2">₹{task.rewardAmount}</Badge>
                      <Badge bg="warning">{task.estimatedTimeMinutes} min</Badge>
                    </div>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="h4 text-success mb-0">₹{task.rewardAmount}</div>
                    <small className="text-muted">Reward Amount</small>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Requirements Reminder */}
            <Card className="mb-4">
              <Card.Header>
                <h6 className="mb-0">
                  <i className="fas fa-list-check me-2"></i>
                  Task Requirements
                </h6>
              </Card.Header>
              <Card.Body>
                <ul className="mb-0">
                  {task.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))}
                </ul>
              </Card.Body>
            </Card>

            {success && (
              <Alert variant="success" className="mb-4">
                <i className="fas fa-check-circle me-2"></i>
                {success}
              </Alert>
            )}

            {error && (
              <Alert variant="danger" className="mb-4">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              {/* Submission Description */}
              <Form.Group className="mb-4">
                <Form.Label>
                  <strong>Describe your completion *</strong>
                  <small className="text-muted ms-2">(min 10 characters)</small>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={submissionText}
                  onChange={(e) => setSubmissionText(e.target.value)}
                  placeholder="Please describe how you completed this task, what steps you followed, and any relevant details..."
                  disabled={loading}
                  required
                  minLength={10}
                  maxLength={2000}
                />
                <Form.Text className="text-muted">
                  {submissionText.length}/2000 characters
                </Form.Text>
              </Form.Group>

              {/* Completion Time */}
              <Form.Group className="mb-4">
                <Form.Label>Time taken (minutes) - Optional</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="480"
                  value={completionTime}
                  onChange={(e) => setCompletionTime(e.target.value ? parseInt(e.target.value) : '')}
                  placeholder="How many minutes did it take?"
                  disabled={loading}
                />
              </Form.Group>

              {/* File Upload */}
              <Form.Group className="mb-4">
                <Form.Label>
                  <strong>Upload Proof Files *</strong>
                  <small className="text-muted ms-2">(Screenshots, documents, etc.)</small>
                </Form.Label>
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileChange}
                  disabled={loading || proofFiles.length >= 5}
                />
                <Form.Text className="text-muted">
                  Supported: Images (JPG, PNG, GIF, WebP), PDF, Word documents. Max 5 files, 10MB each.
                </Form.Text>
              </Form.Group>

              {/* Selected Files */}
              {proofFiles.length > 0 && (
                <div className="mb-4">
                  <h6>Selected Files ({proofFiles.length}/5):</h6>
                  <Row>
                    {proofFiles.map((file, index) => (
                      <Col md={6} key={index} className="mb-2">
                        <Card className="h-100">
                          <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                              <i className={`${getFileIcon(file)} text-primary me-2`}></i>
                              <div className="flex-grow-1 me-2">
                                <div className="fw-bold text-truncate" style={{ maxWidth: '150px' }}>
                                  {file.name}
                                </div>
                                <small className="text-muted">
                                  {formatFileSize(file.size)}
                                </small>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeFile(index)}
                                disabled={loading}
                              >
                                <i className="fas fa-times"></i>
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {/* Upload Progress */}
              {loading && uploadProgress > 0 && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Uploading files...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <ProgressBar 
                    now={uploadProgress} 
                    variant={uploadProgress === 100 ? 'success' : 'primary'}
                  />
                </div>
              )}
            </Form>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="success" 
          onClick={handleSubmit}
          disabled={loading || !task || submissionText.trim().length < 10 || proofFiles.length === 0}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              {uploadProgress > 0 ? `Uploading ${uploadProgress}%` : 'Submitting...'}
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane me-2"></i>
              Submit Task
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TaskSubmissionModal;